import { useEffect, useState } from "react";
import { Sliders, Loader2, FileJson, Database, ToggleLeft, ChevronDown } from "lucide-react";
import { fetchRules } from "@/lib/api";
import { PROVINCE_COLORS } from "@/lib/constants";

export default function RulesEngine() {
  const [rules, setRules] = useState(null);
  const [openGrid, setOpenGrid] = useState(null);

  useEffect(() => {
    fetchRules().then(setRules).catch(() => setRules(null));
  }, []);

  if (!rules) {
    return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-slate-300" /></div>;
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#0F172A] text-white"><Sliders className="h-5 w-5" /></span>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Rules engine</h1>
          <p className="text-slate-600">Program criteria, point tables, cutoffs and stream status are defined in an editable configuration.</p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <MetaTile icon={FileJson} label="Config version" value={rules.meta.version} />
        <MetaTile icon={Database} label="Streams defined" value={rules.streams.length} />
        <MetaTile icon={ToggleLeft} label="Last updated" value={rules.meta.last_updated} />
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
        <p><strong className="text-slate-900">Admin architecture.</strong> {rules.meta.note} All values on this page are read from <code className="rounded bg-slate-200 px-1.5 py-0.5 font-mono-tech text-xs">backend/rules/config.json</code>. Updating that file changes points, thresholds and which streams are active — no application code changes required.</p>
      </div>

      {/* Point grids */}
      <h2 className="mt-10 font-heading text-2xl font-bold text-slate-900">Point grids</h2>
      <div className="mt-4 space-y-3">
        {Object.entries(rules.grids).map(([key, grid]) => (
          <div key={key} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`grid-${key}`}>
            <button onClick={() => setOpenGrid(openGrid === key ? null : key)}
              className="flex w-full items-center justify-between px-5 py-4 text-left">
              <div>
                <div className="font-heading font-semibold text-slate-900">{grid.label}</div>
                <div className="text-xs text-slate-500">Max {grid.max_points} points · {grid.factors.length} factors</div>
              </div>
              <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${openGrid === key ? "rotate-180" : ""}`} />
            </button>
            {openGrid === key && (
              <div className="border-t border-slate-100 px-5 py-4">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-slate-500">
                      <th className="pb-2 font-semibold">Factor</th>
                      <th className="pb-2 font-semibold">Type</th>
                      <th className="pb-2 text-right font-semibold">Allocation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {grid.factors.map((f) => (
                      <tr key={f.id}>
                        <td className="py-2 font-medium text-slate-800">{f.label}</td>
                        <td className="py-2 text-slate-500">{f.type}</td>
                        <td className="py-2 text-right font-mono-tech text-xs text-slate-600">{formatAlloc(f)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Streams config */}
      <h2 className="mt-10 font-heading text-2xl font-bold text-slate-900">Stream definitions</h2>
      <div className="mt-4 space-y-3">
        {rules.streams.map((s) => (
          <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`rule-stream-${s.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: PROVINCE_COLORS[s.province] }}>{s.province}</span>
              <span className="font-heading font-semibold text-slate-900">{s.name}</span>
              <span className={`ml-auto rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                s.status === "active" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : s.status === "paused" ? "border-amber-200 bg-amber-50 text-amber-700"
                : "border-slate-200 bg-slate-100 text-slate-600"}`}>{s.status}</span>
            </div>
            <div className="mt-3 grid gap-x-8 gap-y-1 text-sm sm:grid-cols-2">
              <Row k="Scoring model" v={s.scoring_model} />
              <Row k="Cutoff / target" v={s.cutoff != null ? s.cutoff : "—"} />
              <Row k="Grid" v={s.grid || "—"} />
              <Row k="Eligibility rules" v={`${s.eligibility.length} conditions`} />
            </div>
            <details className="mt-3 group">
              <summary className="cursor-pointer text-xs font-semibold text-[#C8102E]">View eligibility conditions</summary>
              <ul className="mt-2 space-y-1">
                {s.eligibility.map((c) => (
                  <li key={c.id} className="flex items-center justify-between rounded bg-slate-50 px-3 py-1.5 text-xs">
                    <span className="text-slate-700">{c.label} <span className="text-slate-400">({c.type}{c.value != null ? ` = ${JSON.stringify(c.value)}` : ""})</span></span>
                    <span className={`font-semibold ${c.severity === "hard" ? "text-rose-600" : "text-amber-600"}`}>{c.severity}</span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetaTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">{label}</span></div>
      <div className="mt-1 font-mono-tech text-lg font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between border-b border-slate-50 py-1"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span></div>;
}

function formatAlloc(f) {
  if (f.type === "boolean") return `+${f.points}`;
  if (f.type === "map") return `${Math.min(...Object.values(f.map))}–${Math.max(...Object.values(f.map))}`;
  if (f.type === "band") { const pts = f.bands.map((b) => b.points); return `${Math.min(...pts)}–${Math.max(...pts)}`; }
  return "";
}
